import { motion } from "framer-motion";
import { MessageSquare, CalendarClock, Stethoscope, MonitorPlay } from "lucide-react";

const STEPS = [
  {
    n: "01",
    icon: MessageSquare,
    color: "#3B82F6",
    title: "Scrivi su WhatsApp",
    text: "Indica la zona, il tipo di paziente e il medico generalista o specialista richiesto.",
  },
  {
    n: "02",
    icon: CalendarClock,
    color: "#FF8A5B",
    title: "Ricevi disponibilità e prezzo",
    text: "Verifichiamo l’infermiere più vicino e il primo medico compatibile disponibile.",
  },
  {
    n: "03",
    icon: Stethoscope,
    color: "#FF7EB3",
    title: "L’infermiere arriva con TytoCare",
    text: "L’infermiere raggiunge il domicilio, rileva i parametri e prepara gli strumenti diagnostici.",
  },
  {
    n: "04",
    icon: MonitorPlay,
    color: "#D4F84A",
    title: "Il medico effettua la visita",
    text: "Il medico si collega in diretta, guida l’esame e rilascia il referto e le indicazioni cliniche.",
    highlight: true,
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 32 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function HowItWorks() {
  return (
    <section
      id="come-funziona"
      className="relative bg-gradient-to-b from-white to-[#F6F4FA] py-20 sm:py-28"
      data-testid="how-it-works"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Heading + Device */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-[#5B2C91] shadow-[0_8px_30px_rgb(0,0,0,0.04)] ring-1 ring-[#5B2C91]/10"
            >
              <span className="h-2 w-2 rounded-full bg-[#D4F84A]" />
              Come funziona
            </motion.span>

            <motion.h2
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="font-heading mt-5 text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#1F152E]"
              data-testid="how-title"
            >
              Un ambulatorio digitale portato direttamente a casa
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-5 max-w-2xl text-base sm:text-lg leading-relaxed text-[#4A4059]"
              data-testid="how-subtitle"
            >
              Tu richiedi la visita. Noi sincronizziamo l’infermiere territoriale, TytoCare e il medico
              più adatto.
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 order-1 lg:order-2 lg:justify-self-end w-full max-w-[420px] mx-auto lg:mx-0"
          >
            <div className="overflow-hidden rounded-[1.75rem] shadow-[0_24px_60px_rgba(91,44,145,0.22)] ring-1 ring-white/60">
              <img
                src="/generated/tytocare-device-showcase.png"
                alt="Dispositivo TytoCare e i controlli disponibili: cuore, pelle, frequenza cardiaca, gola e bocca, polmoni, temperatura, timpano, addome"
                className="w-full h-auto block"
                data-testid="how-device-image"
              />
            </div>
          </motion.div>
        </div>

        {/* Steps */}
        <div className="relative mt-14 lg:mt-20">
          {/* Desktop connecting line */}
          <div className="hidden lg:block absolute top-10 left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-transparent via-[#5B2C91]/40 to-transparent" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              const highlight = step.highlight;
              return (
                <motion.div
                  key={step.n}
                  custom={i}
                  variants={cardVariants}
                  initial="hidden"
                  animate="visible"
                  className="relative"
                  data-testid={`how-step-${step.n}`}
                >
                  {/* Mobile vertical connector */}
                  {i < STEPS.length - 1 && (
                    <span className="md:hidden absolute left-1/2 -bottom-6 h-6 w-px -translate-x-1/2 bg-[#5B2C91]/20" />
                  )}

                  <div
                    className={`group h-full rounded-3xl p-6 sm:p-7 transition-transform duration-300 hover:-translate-y-1.5 ${
                      highlight
                        ? "bg-[#5B2C91] shadow-[0_24px_60px_rgba(91,44,145,0.35)]"
                        : "bg-white ring-1 ring-[#5B2C91]/8 shadow-[0_8px_30px_rgb(91,44,145,0.05)]"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span
                        className={`font-heading text-4xl sm:text-5xl font-bold ${
                          highlight ? "text-white/25" : "text-[#5B2C91]/15"
                        }`}
                      >
                        {step.n}
                      </span>
                      <span
                        className="inline-flex h-12 w-12 items-center justify-center rounded-2xl"
                        style={{
                          backgroundColor: highlight ? "rgba(212,248,74,0.18)" : `${step.color}1A`,
                          color: step.color,
                        }}
                      >
                        <Icon className="h-6 w-6" />
                      </span>
                    </div>

                    <h3
                      className={`font-heading mt-6 text-xl font-semibold ${
                        highlight ? "text-white" : "text-[#1F152E]"
                      }`}
                    >
                      {step.title}
                    </h3>
                    <p
                      className={`mt-2.5 text-sm leading-relaxed ${
                        highlight ? "text-white/80" : "text-[#4A4059]"
                      }`}
                    >
                      {step.text}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
